import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom-v5-compat";
import PropTypes from "prop-types";
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  Link,
  IconButton,
  Typography,
  Button,
} from "@mui/material";
import {
  Timer,
  SupervisorAccount,
  ListAlt,
  List,
  Notifications,
  LocalOffer,
  CameraAlt,
  Share,
  AccountTree,
  DataUsage,
  CarRental,
  SettingsInputComponent,
  MenuBook,
  DeviceHub,
  LocalShipping,
  Grading,
  AirplaneTicket,
  CardMembership,
  GridOn,
  CalendarMonth,
  Crop75,
  Build,
  History,
} from "@mui/icons-material";

import DirectionsCar from "@mui/icons-material/DirectionsCar";
import CategoryIcon from "@mui/icons-material/Category";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FileCopyIcon from "@mui/icons-material/FileCopy";

import {
  Person,
  BadgeOutlined,
  SettingsInputAntenna,
  LocationOn,
  Group,
  Code,
  AccountBalance,
  Payments,
} from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";

import ChartSquareBarIcon from "../../icons/ChartSquareBar";

import UserIcon from "../../icons/User";
import Business from "@mui/icons-material/Business";

import People from "@mui/icons-material/People";
import ChevronLeftIcon from "src/icons/ChevronLeft";
import ChevronRightIcon from "src/icons/ChevronRight";
import BorderClearIcon from "@mui/icons-material/BorderClear";
import colors from "src/colors";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

import NavSection from "../NavSection";
import Scrollbar from "../Scrollbar";
import { AcUnit, DriveEta, Warning } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

import { LicenseInfo } from "@mui/x-data-grid-premium";

import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { InputAdornment, TextField } from "@mui/material";

LicenseInfo.setLicenseKey(
  "1abd02f27ab877440d9941419b7b3c27Tz0xMTI0NDIsRT0xNzc3OTM5MTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y",
);

const DRAWER_WIDTH = 280;
const AUTO_MINIMISE_URL = [/\/dashboard\/assets\/device[^ ]*/i];

const openedMixin = (theme) => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(10)} + 1px)`,
  minWidth: "80px",
});

const ExpandableDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const sections = [
  {
    title: "Overview",
    items: [
      /*
      {
        title: "Dashboard",
        path: "/dashboard/fleet",
        icon: <ChartSquareBarIcon fontSize="small" />,
      },
      */
      {
        title: "Fleet & Asset Map",
        path: "/dashboard/assets",
        icon: <DriveEta fontSize="small" />,
      },
      {
        title: "Notifications",
        path: "/dashboard/notifications",
        icon: <Notifications fontSize="small" />,
      },
      {
        title: "Dashcam Events",
        path: "/dashboard/dashcam-events",
        icon: <CameraAlt fontSize="small" />,
        feature: "dashcam",
      },
    ],
  },
  {
    title: "Reporting & Insights",
    items: [
      {
        title: "Reporting",
        path: "/dashboard/reports",
        icon: <ListAlt fontSize="small" />,
        feature: "reporting",
      },
      {
        title: "Cold Chain",
        path: "/dashboard/environment",
        icon: <AcUnit fontSize="small" />,
        feature: "cold_chain",
      },
      {
        title: "Coverage Analysis",
        path: "/coverage-map",
        icon: <Crop75 fontSize="small" />,
      },
      {
        title: "Automations",
        path: "/dashboard/automations",
        icon: <AccountTreeIcon fontSize="small" />,
        feature: "automation",
      },
      {
        title: "Insight Tags",
        path: "/dashboard/insight-tags",
        feature: "insight_tags",
        icon: <LocalOffer fontSize="small" />,
      },
    ],
  },

  {
    title: "Maintenance",
    items: [
      {
        title: "Maintenance Board",
        path: "/dashboard/maintenance-board",
        icon: <Build fontSize="small" />, // Kept as is - wrench/tool icon is appropriate
        feature: "vehicle_task_management",
      },
      {
        title: "Vehicle Details & Service",
        path: "/dashboard/vehicle-maintenance",
        icon: <DirectionsCar fontSize="small" />, // Added vehicle icon
        feature: "vehicle_task_management",
      },
      {
        title: "Vehicle Lease Agreements",
        path: "/dashboard/vehicle-lease-agreements",
        icon: <CarRental fontSize="small" />,
        feature: "vehicle_task_management",
      },
      {
        title: "Tasks",
        path: "/dashboard/tasks",
        icon: <AssignmentIcon fontSize="small" />, // Changed to assignment/task icon
        feature: "vehicle_task_management",
      },
      {
        title: "Task Templates",
        path: "/dashboard/task-templates",
        icon: <FileCopyIcon fontSize="small" />, // Changed to template/copy icon
        feature: "vehicle_task_management",
      },
    ],
  },

  {
    title: "Fleet & Assets",
    items: [
      {
        title: "Site Board",
        path: "/dashboard/asset-board",
        icon: <GridOn fontSize="small" />,
      },

      {
        title: "Trips",
        path: "/dashboard/trips",
        icon: <Share fontSize="small" />,
      },
      {
        title: "Trip Assignment",
        path: "/dashboard/trip-assignment",
        icon: <AssignmentIcon fontSize="small" />,
        feature: "logbook",
      },
      {
        title: "Inputs",
        path: "/dashboard/inputs",
        icon: <SettingsInputComponent fontSize="small" />,
        feature: "inputs",
      },
      {
        title: "Timeline",
        path: "/dashboard/timeline",
        icon: <SettingsInputComponent fontSize="small" />,
      },
      {
        title: "Logbook",
        path: "/dashboard/logbook",
        icon: <MenuBook fontSize="small" />,
        feature: "logbook",
      },
      {
        title: "Driver Safety",
        path: "/dashboard/driversafety",
        icon: <Warning fontSize="small" />,
        feature: "driver_safety",
      },

      {
        title: "Overview Map",
        path: "/dashboard/overview-map",
        icon: <AccountTree fontSize="small" />,
      },
      {
        title: "Beacon Overview Map",
        path: "/dashboard/beacon-overview-map",
        icon: <AccountTree fontSize="small" />,
      },
    ],
  },

  {
    title: "People & Operations",
    items: [
      {
        title: "Driver Management",
        path: "/dashboard/drivermanagement",
        icon: <People fontSize="small" />,
        feature: "driver_management",
      },
      {
        title: "Timesheet",
        path: "/dashboard/timesheet",
        icon: <Timer fontSize="small" />,
        feature: "timesheet",
        children: [
          {
            title: "View Timesheet",
            path: "/dashboard/timesheet/view",
            permissions: "review_timesheet",
          },
          {
            title: "Payroll",
            path: "/dashboard/timesheet/payroll",
            permissions: "admin_timesheet",
          },
          {
            title: "Review Timesheet",
            path: "/dashboard/timesheet/review",
            permissions: "access_timesheet",
          },
          {
            title: "Reports",
            path: "/dashboard/timesheet/reports",
            icon: <Warning fontSize="small" />,
            permission: "access_timesheet",
            children: [
              {
                title: "Job Report",
                path: "/dashboard/timesheet/reports/job",
                permission: "access_timesheet",
              },
              {
                title: "Structure Timesheet",
                path: "/dashboard/timesheet/reports/structure",
                permission: "access_timesheet",
              },
              {
                title: "Overtime Timesheet",
                path: "/dashboard/timesheet/reports/overtime",
                permission: "access_timesheet",
              },
              {
                title: "Missing Shifts",
                path: "/dashboard/timesheet/reports/missingShifts",
                permission: "access_timesheet",
              },
              {
                title: "No Login",
                path: "/dashboard/timesheet/reports/noLogin",
                permission: "access_timesheet",
              },
            ],
          },
        ],
      },
      {
        title: "Leave",
        path: "/dashboard/timesheet/leave-requests",
        icon: <CalendarMonth fontSize="small" />,
        feature: "timesheet",
        children: [
          {
            title: "All Leave Requests",
            path: "/dashboard/timesheet/all-leave-requests?admin=true",
            icon: <CalendarMonth fontSize="small" />,
          },
          {
            title: "Leave Requests",
            path: "/dashboard/timesheet/leave-requests",
            icon: <CalendarMonth fontSize="small" />,
          },
          {
            title: "Leave Calendar",
            path: "/dashboard/timesheet/leave-calendar",
            icon: <CalendarMonth fontSize="small" />,
          },
        ],
      },
      {
        title: "Tickets",
        path: "/dashboard/tickets",
        icon: <CardMembership fontSize="small" />,
        feature: "timesheet",
        children: [
          {
            title: "Ticket Admin",
            path: "/dashboard/tickets/admin",
            permissions: "tickets_edit",
          },
          {
            title: "Matrix",
            path: "/dashboard/tickets/matrix",
            permissions: "tickets_view",
          },
          {
            title: "Approvals",
            path: "/dashboard/tickets/approvals",
            permissions: "tickets_add",
          },
        ],
      },
      {
        title: "Forms",
        icon: <ListAlt fontSize="small" />,
        feature: "forms",
        children: [
          {
            title: "Admin",
            path: "/dashboard/forms/admin",
            permission: "forms_add",
          },
          {
            title: "Complete",
            path: "/dashboard/forms/selection",
            permission: "complete_forms",
          },
          {
            title: "View Completed",
            path: "/dashboard/forms/completed",
            permission: "forms_view",
          },
          {
            title: "Review Forms",
            path: "/dashboard/forms/review",
            permission: "forms_edit",
          },
        ],
      },
      {
        title: "Geofences / Sites",
        path: "/dashboard/geofences",
        icon: <BorderClearIcon fontSize="small" />,
      },
    ],
  },
  {
    title: "Admin",
    items: [
      {
        title: "Company",
        path: "/dashboard/company_settings",
        icon: <Business fontSize="small" />,
        permission: "edit_permissions",
      },
      {
        title: "People",
        path: "/dashboard/admin/people",
        icon: <Person fontSize="small" />,
        permission: "people_view",
      },
      {
        title: "Beacons",
        path: "/dashboard/beacons",
        icon: <DeviceHub fontSize="small" />,
        feature: "ble_beacons",
      },
      {
        title: "Beacon Types",
        path: "/dashboard/beacon-config",
        icon: <SettingsInputAntenna fontSize="small" />,
        feature: "ble_beacons",
      },
      {
        title: "Driver Tags",
        path: "/dashboard/driver-tags",
        icon: <BadgeOutlined fontSize="small" />,
        feature: "driver_management",
      },
      {
        title: "Jobsites",
        path: "/dashboard/admin/jobsites",
        icon: <LocationOn fontSize="small" />,
        permission: "jobsites_view",
      },
      /*
      {
        title: "Site Directory",
        path: "/dashboard/site-directory",
        icon: <CalendarMonth fontSize="small" />,
        permission: "jobsites_view",
      },
      {
        title: "Vehicle Assignment Settings",
        path: "/dashboard/vehicle-assignment/settings",
        icon: <SettingsInputComponent fontSize="small" />,
        permission: "jobsites_view",
      },
      {
        title: "Route Planning",
        path: "/dashboard/vehicle-assignment/routes",
        icon: <AccountTree fontSize="small" />,
        permission: "jobsites_view",
      },
      {
        title: "Route History",
        path: "/dashboard/vehicle-assignment/history",
        icon: <History fontSize="small" />,
        permission: "jobsites_view",
      },
      {
        title: "Deviation Review",
        path: "/dashboard/vehicle-assignment/deviations",
        icon: <Warning fontSize="small" />,
        permission: "jobsites_view",
      },
      */
      {
        title: "Groups",
        path: "/dashboard/admin/groups",
        icon: <Group fontSize="small" />,
        permission: "groups_view",
      },
      {
        title: "Jobcodes",
        path: "/dashboard/admin/jobcodes",
        icon: <Code fontSize="small" />,
        permission: "codes_view",
      },
      {
        title: "Account / Device Linking",
        path: "/dashboard/admin/accdevlink",
        icon: <Link fontSize="small" />,
      },
      {
        title: "Allowances",
        path: "/dashboard/admin/allowances",
        icon: <AccountBalance fontSize="small" />,
        permission: "admin_timesheet",
        feature: "timesheet",
      },
      {
        title: "Payroll Groups",
        path: "/dashboard/admin/payrollgroups",
        icon: <Payments fontSize="small" />,
        permission: "admin_timesheet",
        feature: "timesheet",
      },
    ],
  },
  {
    title: "Moncrete",
    feature: "concrete_curing",
    items: [
      {
        title: "Moncrete",
        path: "/dashboard/concrete/dashboard",
        icon: <Timer fontSize="small" />,
        children: [
          {
            title: "Dashboard",
            path: "/dashboard/concrete/dashboard",
          },
          {
            title: "Admin",
            path: "/dashboard/concrete",
            icon: <Warning fontSize="small" />,
            children: [
              { title: "Mix Design", path: "/dashboard/concrete/mix-designs" },
              {
                title: "Mass Concrete Limits",
                path: "/dashboard/concrete/mass-concrete-limits",
              },
              {
                title: "Evaporation Limits",
                path: "/dashboard/concrete/evaporation-limits",
              },
              {
                title: "Maturity Inputs",
                path: "/dashboard/concrete/maturity-inputs",
              },
              { title: "Pours", path: "/dashboard/concrete/Pours" },
              { title: "Sensors", path: "/dashboard/concrete/sensors" },
            ],
          },
        ],
      },
    ],
  },

  {
    title: "My Account",
    items: [
      {
        title: "My Account",
        path: "/dashboard/account",
        icon: <UserIcon fontSize="small" />,
      },
      {
        title: "Subscriptions",
        path: "/dashboard/subscriptions",
        icon: <LocalOffer fontSize="small" />,
      },
    ],
  },
];

const DashboardSidebar = (props) => {
  const { onMobileClose, openMobile } = props;
  const [expanded, setExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredSections, setFilteredSections] = useState([]);
  const location = useLocation();
  const { user } = useAuth();

  // 3. Add this function to filter menu items based on search
  const filterMenuItems = (query) => {
    const isTsAdmin = Boolean(user?.tsadmin);
    const isAllowedTsAdminItem = (item) =>
      item.path === "/dashboard/assets" ||
      item.title === "Fleet & Asset List" ||
      item.title === "Fleet & Asset Map";

    if (!query) {
      return isTsAdmin
        ? sections
            .map((section) => ({
              ...section,
              items: section.items.filter((item) => isAllowedTsAdminItem(item)),
            }))
            .filter((section) => section.items.length > 0)
        : sections;
    }

    const lowercaseQuery = query.toLowerCase();

    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            (!isTsAdmin || isAllowedTsAdminItem(item)) &&
            (item.title.toLowerCase().includes(lowercaseQuery) ||
              (item.children &&
                item.children.some((child) =>
                  child.title.toLowerCase().includes(lowercaseQuery),
                ))),
        ),
      }))
      .filter((section) => section.items.length > 0);
  };

  const { features_enabled = {} } = user;

  // 4. Update useEffect to handle filtering
  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }

    for (let i = 0; i < AUTO_MINIMISE_URL.length; i++) {
      let regex = new RegExp(AUTO_MINIMISE_URL[i]);
      if (regex.test(location.pathname)) {
        setExpanded(false);
        break;
      }
    }

    setFilteredSections(filterMenuItems(searchQuery));
  }, [location.pathname, searchQuery, user.tsadmin]);

  // 5. Add the handleSearch function
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  // 6. Add the clearSearch function
  const clearSearch = () => {
    setSearchQuery("");
  };

  const generateContent = (mobileMode) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Hidden lgUp>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 2,
          }}
        >
          <RouterLink to="/">{/*<Logo color="black" />*/}</RouterLink>
        </Box>
      </Hidden>
      <Box sx={{ p: 2, position: "relative" }}>
        {expanded || mobileMode ? (
          <Box
            sx={{
              mt: { xs: 4, lg: 0 },
              alignItems: "center",
              backgroundColor: "background.default",
              borderRadius: 1,
              display: "flex",
              overflow: "hidden",
              p: 2,
            }}
          >
            <RouterLink to="/dashboard/account">
              <Avatar
                src={`${user.photo}`}
                sx={{
                  cursor: "pointer",
                  height: 48,
                  width: 48,
                }}
              />
            </RouterLink>
            <Box sx={{ ml: 2 }}>
              <Typography color="textPrimary" variant="subtitle2">
                {user.name}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                <Link color="primary" component={RouterLink} to="/dashboard">
                  {user.first_name} {user.last_name}
                </Link>
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              py: 2,
            }}
          >
            <RouterLink to="/dashboard/account">
              <Avatar
                src={`data:image/png;base64,${user.avatar}`}
                sx={{
                  cursor: "pointer",
                  height: 48,
                  width: 48,
                }}
              />
            </RouterLink>
          </Box>
        )}
        <IconButton
          onClick={() => {
            setExpanded((prevState) => !prevState);
          }}
          sx={{
            display: { xs: "none", sm: "none", md: "none", lg: "flex" },
            position: "absolute",
            right: 0,
            bottom: 0,
            transform: "translate(50%, 50%)",
            zIndex: 1000,
            backgroundColor: colors.white,
            border: "1px solid rgba(0, 0, 0, 0.12)",
          }}
          color="primary"
        >
          {!expanded ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ChevronLeftIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      <Divider />

      {(expanded || mobileMode) && (
        <Box sx={{ px: 2, pt: 2 }}>
          <TextField
            fullWidth
            placeholder="Search menu..."
            value={searchQuery}
            onChange={handleSearch}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={clearSearch} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </Box>
      )}
      <Scrollbar options={{ suppressScrollX: true }}>
        <Box sx={{ p: 2 }}>
          {filteredSections.map((section) => {
            return (
              <>
                {(user?.permissions[section.permission] ||
                  !section.permission) &&
                  (features_enabled[section.feature] || !section.feature) && (
                    <NavSection
                      user={user}
                      setExpanded={setExpanded}
                      expanded={expanded || mobileMode}
                      key={section.title}
                      pathname={location.pathname}
                      sx={{
                        "& + &": {
                          mt: 3,
                        },
                      }}
                      {...section}
                    />
                  )}
              </>
            );
          })}
        </Box>
        <Divider />
      </Scrollbar>
      <Box sx={{ p: 2, display: expanded ? "block" : "none" }}>
        <Typography color="textPrimary" variant="subtitle2">
          Require Support or Found a Bug?
        </Typography>
        <Typography color="textSecondary" variant="body2">
          Log an issue with our support team.
        </Typography>
        <Button
          color="primary"
          fullWidth
          sx={{ mt: 2 }}
          component="a"
          href="https://support.au.monger.tech/servicedesk/customer/portal/1"
          variant="contained"
          target="_blank"
          rel="noopener noreferrer"
        >
          Report Issue
        </Button>
        <Box />
      </Box>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={openMobile}
          PaperProps={{
            sx: {
              backgroundColor: "background.paper",
              width: DRAWER_WIDTH,
            },
          }}
          variant="temporary"
        >
          {generateContent(true)}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <ExpandableDrawer
          style={{ overflow: "visible", zIndex: 1222 }}
          anchor="left"
          open={expanded}
          onClose={() => {
            setExpanded(true);
          }}
          PaperProps={{
            sx: {
              backgroundColor: "background.paper",
              height: "calc(100% - 64px) !important",
              top: "64px !Important",
              overflow: "visible !important",
              width: DRAWER_WIDTH,
            },
          }}
          variant="permanent"
        >
          {generateContent()}
        </ExpandableDrawer>
      </Hidden>
    </>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default DashboardSidebar;
